import { createContext, useContext, useState, useEffect, ReactNode } from "react"

interface FavoritesContextType {
  favorites: string[]
  addFavorite: (toolId: string) => void
  removeFavorite: (toolId: string) => void
  toggleFavorite: (toolId: string) => void
  isFavorite: (toolId: string) => boolean
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined)

export function useFavorites() {
  const context = useContext(FavoritesContext)
  if (!context) {
    throw new Error("useFavorites must be used within a FavoritesProvider")
  }
  return context
}

interface FavoritesProviderProps {
  children: ReactNode
}

export function FavoritesProvider({ children }: FavoritesProviderProps) {
  const [favorites, setFavorites] = useState<string[]>([])
  const [initialized, setInitialized] = useState(false)

  // Load favorites from localStorage on mount
  useEffect(() => {
    try {
      const savedFavorites = localStorage.getItem("favorites")
      if (savedFavorites) {
        setFavorites(JSON.parse(savedFavorites))
      }
    } catch (error) {
      console.error("Failed to load favorites from localStorage:", error)
    } finally {
      setInitialized(true)
    }
  }, [])

  // Save favorites to localStorage whenever it changes (but only after initialization)
  useEffect(() => {
    if (!initialized) return

    try {
      localStorage.setItem("favorites", JSON.stringify(favorites))
    } catch (error) {
      console.error("Failed to save favorites to localStorage:", error)
    }
  }, [favorites, initialized])

  const addFavorite = (toolId: string) => {
    setFavorites(prev => {
      if (prev.includes(toolId)) {
        return prev
      }
      return [...prev, toolId]
    })
  }

  const removeFavorite = (toolId: string) => {
    setFavorites(prev => prev.filter(id => id !== toolId))
  }

  const toggleFavorite = (toolId: string) => {
    setFavorites(prev => {
      if (prev.includes(toolId)) {
        return prev.filter(id => id !== toolId)
      }
      return [...prev, toolId]
    })
  }

  const isFavorite = (toolId: string) => {
    return favorites.includes(toolId)
  }

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        addFavorite,
        removeFavorite,
        toggleFavorite,
        isFavorite,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  )
} 